/*jshint browserify: true */
'use strict';

/**
 * @name PdfElement
 *
 * A PDF element is an extra "thing" that gets rendered on-top of the PDF.
 * For example, a text element will render some text, an image element will render an image, etc.
 * Note that position properties are in PDF coordinate space, where 0,0 is bottom-left.
 *
 * @prop {Number} x - The x coordinate of the left edge of the element, in PDF pixels
 * @prop {Number} y - The y coordinate of the bottom edge of the element, in PDF pixels
 * @prop {Number} width - The width of the element, in PDF pixels
 * @prop {Number} height - The height of the element, in PDF pixels
 */
function PdfElement() {
    this.x = 0;
    this.y = 0;
    this.width = 0;
    this.height = 0;
    this.pageNumber = 1;
}

var edgeTestTolerance = 2;

PdfElement.colourSettings = {
    borderColour: '#FFFFFF',
    borderAlpha: 0.8,
    borderWidth: 2,
    borderColourSelected: '#FF0000',
    borderAlphaSelected: 1,
    borderWidthSelected: 2,
    borderColourHovered: '#FF0000',
    borderAlphaHovered: 1,
    borderWidthHovered: 2,

    fillColour: '#000000',
    fillAlpha: 0.2
};

PdfElement.prototype = {

    /**
     * Render the element to HTML canvas.
     * @param {PageViewport} pdfViewport - A PDF.JS page viewport object
     * @param {CanvasRenderingContext2D} canvasContext - HTML 2D canvas context
     * @param {bool} selected - Whether the element is to be drawn with "selected" style
     * @param {PDFedit.coordinates.Position} mousePosition - Position of mouse relative to the PDF page the element is on
     * @param {PDFedit.uimodes.InteractionMode} interactionMode
     */
    renderToCanvas: function (pdfViewport, canvasContext, selected, mousePosition, interactionMode) {
        var elementRectangle = new coordinates.Rectangle(this.x, this.y, this.width, this.height, pdfViewport);

        var permitHoverStyle = true;
        if (interactionMode instanceof SelectionMode) {
            if (interactionMode._subMode === SelectionMode.subModes.RESIZE ||
                interactionMode._subMode === SelectionMode.subModes.TEXT_SPACING ||
                interactionMode._subMode === SelectionMode.subModes.MOVE) {

                permitHoverStyle = false;
            }
        }

        canvasContext.save();
        if (elementRectangle.positionIsInsideRectangle(mousePosition) && permitHoverStyle) {
            canvasContext.fillStyle = 'blue';
            canvasContext.globalAlpha = 0.3;
        } else {
            canvasContext.fillStyle = PdfElement.colourSettings.fillColour;
            canvasContext.globalAlpha = PdfElement.colourSettings.fillAlpha;
        }
        canvasContext.fillRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);
        canvasContext.restore();

        canvasContext.save();

        if (selected) {
            canvasContext.lineWidth = PdfElement.colourSettings.borderWidthSelected;
            canvasContext.strokeStyle = PdfElement.colourSettings.borderColourSelected;
            canvasContext.globalAlpha = PdfElement.colourSettings.borderAlphaSelected;
            canvasContext.strokeRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);
        } else {
            canvasContext.lineWidth = PdfElement.colourSettings.borderWidth;
            canvasContext.strokeStyle = PdfElement.colourSettings.borderColour;
            canvasContext.globalAlpha = PdfElement.colourSettings.borderAlpha;
            canvasContext.strokeRect(elementRectangle.viewportLeft, elementRectangle.viewportTop, elementRectangle.viewportWidth, elementRectangle.viewportHeight);
        }

        canvasContext.restore();
    },

    /**
     * Test whether the position is within the bounding box of this element.
     *
     * @param {PageViewport} pdfViewport - A PDF.JS page viewport object
     * @param {HTMLElement} viewportElement - The page element that contains the PDF viewport
     * @param {PDFedit.coordinates.Position} position - Position to test
     *
     * @returns {bool} true if position was within the bounding box of this element
     */
    hitTest: function (pdfViewport, viewportElement, position) {
        return position.pdfX >= this.x && position.pdfX <= this.x + this.width && position.pdfY >= this.y && position.pdfY <= this.y + this.height;
    },

    /**
     * Test whether the position is on one of the edges of this element.
     *
     * @param {PageViewport} pdfViewport - A PDF.JS page viewport object
     * @param {HTMLElement} viewportElement - The page element that contains the PDF viewport
     * @param {PDFedit.coordinates.Position} position - Position to test
     *
     * @returns {string} A string representing which edge of the element the position is situated over,
     *   or, if it is not situated over an edge, an empty string. Valid edge descriptions are:
     *   - top-left
     *   - top-right
     *   - bottom-right
     *   - bottom-left
     *   - left
     *   - right
     *   - top
     *   - bottom
     */
    edgeTest: function (pdfViewport, viewportElement, position) {
        var translatedRectangle = coordinates.translatePdfRectangleToViewportRectangle(pdfViewport, this);

        if (position.viewportX >= translatedRectangle.x - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y - edgeTestTolerance) {

            return 'top-left';
        }

        if (position.viewportX >= translatedRectangle.x + translatedRectangle.width - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + translatedRectangle.width + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y - edgeTestTolerance) {

            return 'top-right';
        }

        if (position.viewportX >= translatedRectangle.x + translatedRectangle.width - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + translatedRectangle.width + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + translatedRectangle.height + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y + translatedRectangle.height - edgeTestTolerance) {

            return 'bottom-right';
        }

        if (position.viewportX >= translatedRectangle.x - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + translatedRectangle.height + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y + translatedRectangle.height - edgeTestTolerance) {

            return 'bottom-left';
        }

        if (position.viewportX >= translatedRectangle.x - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + translatedRectangle.height + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y - edgeTestTolerance) {

            return 'left';
        }

        if (position.viewportX >= translatedRectangle.x + translatedRectangle.width - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + translatedRectangle.width + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + translatedRectangle.height + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y - edgeTestTolerance) {

            return 'right';
        }

        if (position.viewportX >= translatedRectangle.x - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + translatedRectangle.width + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y - edgeTestTolerance) {

            return 'top';
        }

        if (position.viewportX >= translatedRectangle.x - edgeTestTolerance &&
            position.viewportX <= translatedRectangle.x + translatedRectangle.width + edgeTestTolerance &&
            position.viewportY <= translatedRectangle.y + translatedRectangle.height + edgeTestTolerance &&
            position.viewportY >= translatedRectangle.y + translatedRectangle.height - edgeTestTolerance) {

            return 'bottom';
        }

        return '';
    }
};

module.exports = PdfElement;

var coordinates = require('../Coordinates');
var SelectionMode = require('../UImodes/SelectionMode.js');